import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { Me, GetMembers } from '../apollo-client/api-query';
import { flattenMembersConnection } from '../utils/member-lookup';
import {
  setAuth,
  setMembers,
  setReports,
  setIsStaffPhysician,
  setIsAdminStaffPhysician,
  setLoadingAuth,
  refetchMe
} from '../store';

// Roles that should not query the `members` list (patients themselves).
// The backend returns Unauthorized for these, so skip the query to avoid
// a noisy error toast during login.
const MEMBER_QUERY_SKIP_ROLES = ['CUSTOMER'];

const useAuth = () => {
  const dispatch = useDispatch();
  const isRefetchMe = useSelector((state) => state.refetchMe);

  const [authQuery] = useState(Me);
  const [isAuthQueryCompleted, setIsAuthQueryCompleted] = useState(false);
  const [authLoadingState, setAuthLoadingState] = useState(true);
  const [authedRoles, setAuthedRoles] = useState(null);

  const {
    data: authData,
    error: authError,
    refetch: refetchAuth
  } = useQuery(authQuery, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data?.me) {
        const me = data.me;
        dispatch(setIsStaffPhysician(me?.roles?.includes('STAFF_PHYSICIAN')));
        dispatch(setIsAdminStaffPhysician(me?.is_staff_physician));
        dispatch(setAuth(me));
        setAuthedRoles(me?.roles || []);
      } else {
        dispatch(setAuth(null));
        dispatch(setReports(null));
        dispatch(setMembers([]));
        setAuthedRoles(null);
      }
      setIsAuthQueryCompleted(true);
      setAuthLoadingState(false);
    },
    onError: () => {
      dispatch(setAuth(null));
      dispatch(setReports(null));
      dispatch(setMembers([]));
      setAuthedRoles(null);
      setIsAuthQueryCompleted(true);
      setAuthLoadingState(false);
    }
  });

  // Query the members list once auth is known and the caller has a non-Customer role.
  // Stores the flattened list in Redux so all components can read it sync.
  //
  // errorPolicy: 'all' — field-level permission errors (e.g. Sales users can't
  // read `firstMeeting` because they lack ReadMeeting) should not wipe out the
  // whole response. Apollo returns partial data with null sub-fields; the UI
  // handles nulls gracefully.
  //
  // context.suppressToast: true — prevents the global Apollo error link from
  // showing an "Unauthorized" toast for each field-level permission error.
  // Those errors are expected for limited-access roles like Sales.
  //
  // NOTE: we deliberately do NOT use onCompleted/onError here. Apollo Client
  // v3.7.x does not fire onCompleted when the response contains GraphQL errors
  // even with errorPolicy: 'all' — which is exactly the Sales scenario. Instead
  // we sync `data` to Redux via a useEffect below, which runs whenever Apollo
  // returns partial data.
  const skipMembers =
    !authedRoles ||
    authedRoles.length === 0 ||
    authedRoles.some(r => MEMBER_QUERY_SKIP_ROLES.includes(r));

  const {
    data: membersData,
    error: membersError,
    refetch: refetchMembers,
  } = useQuery(GetMembers, {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    context: { suppressToast: true },
    skip: skipMembers,
  });

  useEffect(() => {
    if (skipMembers) return;
    if (membersData) {
      // Partial or full data — dispatch whatever came back.
      dispatch(setMembers(flattenMembersConnection(membersData)));
    } else if (membersError) {
      // Hard failure (network error or top-level Unauthorized) and no partial
      // data — clear the list so stale members don't persist.
      dispatch(setMembers([]));
    }
  }, [skipMembers, membersData, membersError, dispatch]);

  /**
   * 🔄 Refetches auth when isRefetchMe is true
   */
  useEffect(() => {
    if (isRefetchMe) {
      refetchAuth()
      .then(({data}) => {
        if (data?.me) {
          const me = data.me;
          dispatch(setIsStaffPhysician(me?.roles?.includes('STAFF_PHYSICIAN')));
          dispatch(setIsAdminStaffPhysician(me?.is_staff_physician));
          dispatch(setAuth(me));
          setAuthedRoles(me?.roles || []);
          // Also refetch members so newly-assigned staff appear on the
          // patient list immediately.
          const shouldRefetchMembers =
            me?.roles?.length > 0 &&
            !me.roles.some(r => MEMBER_QUERY_SKIP_ROLES.includes(r));
          if (shouldRefetchMembers) {
            refetchMembers()
              .then(({ data: membersData }) => {
                dispatch(setMembers(flattenMembersConnection(membersData)));
              })
              .catch(() => dispatch(setMembers([])));
          }
        } else {
          dispatch(setAuth(null));
          dispatch(setReports(null));
          dispatch(setMembers([]));
          setAuthedRoles(null);
        }
        setIsAuthQueryCompleted(true);
        setAuthLoadingState(false);
        dispatch(refetchMe(false))
      })
      .catch(() => {
        dispatch(setAuth(null));
        dispatch(setReports(null));
        dispatch(setMembers([]));
        setAuthedRoles(null);
        setIsAuthQueryCompleted(true);
        setAuthLoadingState(false);
        dispatch(refetchMe(false))
      });
    }
  }, [isRefetchMe, refetchAuth, refetchMembers, dispatch]);

  useEffect(() => {
    dispatch(setLoadingAuth(!(isAuthQueryCompleted && !authLoadingState)));
  }, [dispatch, isAuthQueryCompleted, authLoadingState]);

  return {
    loading: !isAuthQueryCompleted || authLoadingState,
    error: authError,
    user: authData?.me || null
  };
};


export default useAuth;
